/**
 * «Conferma al marketplace» — dice al canale che il pacco è partito e gli
 * passa il tracking, senza passare da Poleepo.
 *
 * Serve perché il write-back diretto (su Mirakl: OR23 tracking + OR24
 * spedito) finora si accendeva solo generando la LDV di un ordine *nativo*, e
 * gli ordini Mirakl nativi in produzione sono zero: entrano tutti dal
 * connettore Poleepo. Il bottone è l'unico modo che ha l'operatore di
 * chiudere l'ordine sul canale quando Poleepo non l'ha propagato.
 */

import { apiPost } from "@/shared/api";
import type { ConfermaCanale } from "./api";

/** I tre shop Mirakl di cui abbiamo le credenziali, per prefisso del canale
 *  commerciale (``raw.source``). Rispecchia ``SHOPS[*].canali`` nel backend
 *  (``shared/mirakl_client.py``); il prefisso invece dell'elenco esatto così
 *  un canale nuovo dello stesso shop (LEROY_MERLIN_PT…) non richiede di
 *  toccare due file.
 *
 *  ⛔ Brico Bravo è Mirakl ma NON è dei nostri: non ha uno shop configurato, e
 *  il logo lo confonde con gli altri. Elencare i prefissi invece di fidarsi
 *  del logo è ciò che lo tiene fuori. */
const CANALI_DIRETTI: ReadonlyArray<readonly [string, string]> = [
  ["MAISONS", "Maisons du Monde"],
  ["LEROY", "Leroy Merlin"],
  ["VENTEUNIQUE", "Vente Unique"],
  // Amazon (IT/ES/FR) dal 2026-08-21. Non passa da Mirakl: la conferma è un
  // feed SP-API, e l'esito arriva dopo — vedi `messaggioConferma`.
  ["AMAZON", "Amazon"],
  // Metro (IT/ES) dal 2026-08-21. Si spedisce per RIGA d'ordine, e il corriere
  // dev'essere un id del dizionario Metro: SDA → `post_italiane` (il link a
  // poste.it funziona davvero per i codici SDA), BRT in Italia col nome libero
  // (Metro lo spegne il 30/10/2026), BRT estero → `dpd`.
  ["METRO", "Metro"],
  // Brico Bravo dal 2026-08-28. Il piu' lineare: si spedisce per ordine e i
  // codici corriere sono esattamente i nostri (italy-sda, brt-it,
  // poste-italiane). Vuole anche il LINK di tracciatura, non solo il numero.
  ["BRICO", "Brico Bravo"],
  // ManoMano dal 2026-08-31: sesto e ultimo. API proprietaria (NON Mirakl),
  // corriere in testo libero.
  ["MANOMANO", "ManoMano"],
  // WooCommerce e' uscito il 2026-10-19: bobgardens.com e' chiuso, i suoi
  // ordini restano ma il sito non si aggiorna piu' (il backend rifiuta).
];

/** Nome del marketplace se questo ordine si può confermare da qui, altrimenti
 *  ``null`` — e allora il bottone NON si renderizza affatto (regola D1: un
 *  tasto disabilitato con il motivo nel tooltip è un tasto che si prova).
 *
 *  Senza tracking non c'è niente da comunicare: confermare a mani vuote manda
 *  al cliente una mail "spedito" e nessun numero da seguire. */
export function nomeCanaleDiretto(order: { raw?: { source?: unknown } | null }): string | null {
  const source = (typeof order.raw?.source === "string" ? order.raw.source : "").toUpperCase();
  if (!source) return null;
  return CANALI_DIRETTI.find(([prefisso]) => source.startsWith(prefisso))?.[1] ?? null;
}

export function canaleDirettoDiUnOrdine(order: {
  trackingCode?: string | null;
  raw?: { source?: unknown } | null;
}): string | null {
  if (!order.trackingCode) return null;
  return nomeCanaleDiretto(order);
}

export function confermaCanale(orderRef: string): Promise<ConfermaCanale> {
  return apiPost<ConfermaCanale>(`/api/orders/${encodeURIComponent(orderRef)}/conferma-canale`);
}

/** Come raccontare l'esito. ``gia_fatto`` è verde, non rosso: il clic
 *  ripetuto è il più frequente di tutti e il marketplace era già a posto. */
export function messaggioConferma(
  esito: ConfermaCanale,
  canale: string,
): { tono: "ok" | "errore"; testo: string } {
  // ⚠️ Amazon: il feed è stato preso in carico, l'ordine NON risulta ancora
  // spedito. Dire «confermato» qui sarebbe una bugia col volto di un
  // successo: l'esito vero lo scrive il job quando arriva il rapporto.
  if (esito.inviato) {
    return {
      tono: "ok",
      testo: `Inviato a ${canale}: l'esito arriva fra qualche minuto, lo trovi sull'ordine`,
    };
  }
  if (esito.confermato) {
    const trk = esito.tracking_code ? ` · tracking ${esito.tracking_code}` : "";
    return { tono: "ok", testo: `${canale} avvisato: ordine spedito${trk}` };
  }
  if (esito.gia_fatto) {
    return { tono: "ok", testo: `${canale} era già aggiornato: niente da fare` };
  }
  // Senza motivo il testo resterebbe «…non è stato aggiornato:» — una frase a
  // metà è peggio di una frase generica, perché sembra un troncamento.
  const motivo = esito.motivo?.trim();
  return {
    tono: "errore",
    testo: motivo
      ? `${canale} NON è stato aggiornato: ${motivo}`
      : `${canale} NON è stato aggiornato (il canale non ha detto perché)`,
  };
}
